import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2,
  ArrowRight, Info,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useCanEdit } from "@/context/AuthContext";
import { downloadFile } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_TONE = {
  created: "text-emerald-700",
  updated: "text-blue-700",
  error: "text-rose-700",
};

/**
 * Bulk import and export.
 *
 * The flow is deliberately two-step. You pick a file, we read all of it and
 * show you exactly what would happen to every row — and only then does the
 * Import button do anything. A bulk import that fails halfway leaves someone
 * with half a customer list and no way to tell which half.
 */
export default function DataPorting({ only }) {
  const canEdit = useCanEdit();
  const [datasets, setDatasets] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/port/datasets").then(({ data }) => setDatasets(data)).catch(() => {});
  }, []);

  const shown = useMemo(
    () => (only ? datasets.filter((d) => only.includes(d.key)) : datasets),
    [datasets, only]);

  const groups = useMemo(() => {
    const out = {};
    shown.forEach((d) => { (out[d.group] ||= []).push(d); });
    return out;
  }, [shown]);

  const grab = async (key, format) => {
    try {
      await downloadFile(`/port/${key}/export`, { format }, `${key}.${format}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const template = async (key, format) => {
    try {
      await downloadFile(`/port/${key}/template`, { format }, `${key}-template.${format}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="data-porting">
      {!only && (
        <p className="max-w-2xl text-sm text-slate-500">
          Bring your existing customers, suppliers, items and opening stock in from a
          spreadsheet, and take anything back out whenever you want. Exports are plain
          data files — a file that comes out of here goes back in unchanged.
        </p>
      )}

      {Object.entries(groups).map(([group, rows]) => (
        <div key={group} className="space-y-3">
          {!only && (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {group}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((d) => (
              <div key={d.key} className="rounded-lg border border-slate-200 bg-white p-4"
                data-testid={`port-card-${d.key}`}>
                <div className="flex items-start gap-2">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-slate-100 text-slate-500">
                    <FileSpreadsheet size={15} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{d.label}</div>
                    <div className="mt-0.5 text-[11px] leading-snug text-slate-500">
                      {d.description}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
                    onClick={() => grab(d.key, "csv")} data-testid={`port-export-csv-${d.key}`}>
                    <Download size={13} /> CSV
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
                    onClick={() => grab(d.key, "xlsx")} data-testid={`port-export-xlsx-${d.key}`}>
                    <Download size={13} /> Excel
                  </Button>
                  {d.importable && canEdit && (
                    <>
                      <button className="text-[11px] text-slate-500 hover:text-slate-800 transition-colors duration-150"
                        onClick={() => template(d.key, "xlsx")}
                        data-testid={`port-template-${d.key}`}>
                        Blank template
                      </button>
                      <Button size="sm" className="ml-auto h-8 gap-1.5 bg-blue-600 text-xs hover:bg-blue-700"
                        onClick={() => setActive(d)} data-testid={`port-import-${d.key}`}>
                        <Upload size={13} /> Import
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {active && <ImportDialog dataset={active} onClose={() => setActive(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ import */
function ImportDialog({ dataset, onClose }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const send = useCallback(async (path, chosen) => {
    const body = new FormData();
    body.append("file", chosen);
    return api.post(`/port/${dataset.key}/${path}`, body);
  }, [dataset.key]);

  const pick = async (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setPreview(null);
    setDone(null);
    setBusy(true);
    try {
      const { data } = await send("validate", chosen);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
      setFile(null);
    }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await send("import", file);
      setDone(data);
      toast.success(`${data.created} added, ${data.updated} updated`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const result = done || preview;
  const problems = (result?.rows || []).filter((r) => r.status === "error");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import {dataset.label.toLowerCase()}</DialogTitle>
          <DialogDescription>
            CSV or Excel. We read the whole file and show you what would happen before
            anything is written.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!done && (
            <div className="rounded-md border border-dashed border-slate-300 px-4 py-5 text-center">
              <input type="file" accept=".csv,.xlsx,.xlsm" ref={fileRef} className="hidden"
                data-testid="import-file"
                onChange={(e) => pick(e.target.files?.[0])} />
              <FileSpreadsheet size={22} className="mx-auto text-slate-300" />
              <p className="mt-2 text-xs text-slate-500">
                {file ? file.name : "Choose a CSV or Excel file"}
              </p>
              <Button size="sm" variant="outline" className="mt-3 gap-1.5"
                onClick={() => fileRef.current?.click()} disabled={busy}
                data-testid="import-choose">
                {busy && !preview ? <Loader2 size={14} className="animate-spin" />
                                  : <Upload size={14} />}
                {file ? "Choose a different file" : "Choose file"}
              </Button>
            </div>
          )}

          {result && (
            <>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Rows", result.total, "text-slate-700"],
                  ["New", result.created, "text-emerald-700"],
                  ["Updated", result.updated, "text-blue-700"],
                  ["Problems", result.failed, result.failed ? "text-rose-700" : "text-slate-400"],
                ].map(([label, value, tone]) => (
                  <div key={label} className="rounded-md border border-slate-200 bg-white py-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
                    <div className={`font-heading text-xl font-bold tabular-nums ${tone}`}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              {result.capacity_error && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle size={14} className="mt-px shrink-0" />
                  <span>{typeof result.capacity_error === "string"
                    ? result.capacity_error
                    : result.capacity_error?.message}</span>
                </div>
              )}

              {!!problems.length && (
                <div className="rounded-md border border-slate-200">
                  <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Rows that {done ? "were skipped" : "will be skipped"}
                  </div>
                  <div className="max-h-52 divide-y divide-slate-100 overflow-y-auto">
                    {problems.slice(0, 100).map((r) => (
                      <div key={r.row} className="flex items-start gap-3 px-3 py-1.5 text-xs">
                        <span className="w-14 shrink-0 font-mono text-slate-400">row {r.row}</span>
                        <span className="w-40 shrink-0 truncate text-slate-600">{r.label || "—"}</span>
                        <span className="text-rose-700">{r.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!done && !!(result.created + result.updated) && (
                <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <Info size={14} className="mt-px shrink-0 text-slate-400" />
                  <span>
                    Nothing has been written yet.
                    {result.failed
                      ? " The rows above will be left out; everything else goes in."
                      : " Every row is good to go."}
                  </span>
                </div>
              )}

              {done && (
                <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  <CheckCircle2 size={14} />
                  Imported. {done.created} added, {done.updated} updated
                  {done.failed ? `, ${done.failed} skipped` : ""}.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{done ? "Close" : "Cancel"}</Button>
          {!done && (
            <Button onClick={commit} className="gap-1.5 bg-blue-600 hover:bg-blue-700"
              data-testid="import-commit"
              disabled={busy || !preview || !(preview.created + preview.updated)}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
              Import {preview ? preview.created + preview.updated : ""} row
              {preview && preview.created + preview.updated === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The same machinery as a pair of buttons, for the top of a list page.
 * `datasetKey` exports; `importKey` (when given) opens the import dialog.
 */
export function PortButtons({ datasetKey, importable = false, label = "" }) {
  const canEdit = useCanEdit();
  const [dataset, setDataset] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!importable) return;
    api.get("/port/datasets")
      .then(({ data }) => setDataset(data.find((d) => d.key === datasetKey) || null))
      .catch(() => {});
  }, [datasetKey, importable]);

  const grab = async (format) => {
    try {
      await downloadFile(`/port/${datasetKey}/export`, { format },
                         `${datasetKey}.${format}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="gap-1.5"
        onClick={() => grab("xlsx")} data-testid={`export-${datasetKey}`}>
        <Download size={15} /> Export{label ? ` ${label}` : ""}
      </Button>
      {importable && canEdit && (
        <Button size="sm" variant="outline" className="gap-1.5"
          onClick={() => setOpen(true)} data-testid={`import-${datasetKey}`}>
          <Upload size={15} /> Import
        </Button>
      )}
      {open && dataset && <ImportDialog dataset={dataset} onClose={() => setOpen(false)} />}
    </>
  );
}
